import { useMemo, useState } from "react";
import PrimaryHeading from '../../components/common/primaryHeading';
import ButtonWithSearch from '../../components/common/buttonWithSearch';
import ImageUpload from '../../components/common/ImageUpload';
import AddButtonSection from '../../components/common/AddButtonSection';
import { showToast } from '../../components/common/toast';
import { TOAST_TYPE, FEATURE_MSG } from "../../constants/toast";
import { commonPostApi } from "../../utils/apiService";
import { API_ENDPOINTS } from "../../utils/apiEndpoints";

function SystemSettings() {
  const [uploadedLogo, setUploadedLogo] = useState(null);
  const [uploadedFullLogo, setUploadedFullLogo] = useState(null);
  const [uploadedFavicon, setUploadedFavicon] = useState(null);
  const [uploadedLoginLogo, setUploadedLoginLogo] = useState(null);
  const [resetKey, setResetKey] = useState(0);
  const [logoRefreshKey, setLogoRefreshKey] = useState(0);
  const [isSaving, setIsSaving] = useState(false);


  const logoUrls = useMemo(() => ({
    mini: `${process.env?.REACT_APP_CURRENT_URL}${API_ENDPOINTS.GET_COMPANY_LOGO}`,
    full: `${process.env?.REACT_APP_CURRENT_URL}${API_ENDPOINTS.GET_COMPANY_FULL_LOGO}`,
    fav: `${process.env?.REACT_APP_CURRENT_URL}${API_ENDPOINTS.GET_FAVICON}`,
    login: `${process.env?.REACT_APP_CURRENT_URL}${API_ENDPOINTS.GET_LOGIN_LOGO}`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [logoRefreshKey]);

  const hasChanges = useMemo(() => {
    return (
      uploadedLogo ||
      uploadedFullLogo ||
      uploadedFavicon ||
      uploadedLoginLogo
    );
  }, [uploadedLogo, uploadedFullLogo, uploadedFavicon, uploadedLoginLogo]);


  const handleLogoSelect = (file) => {
    setUploadedLogo(file);
  };

  const handleFullLogoSelect = (file) => {
    setUploadedFullLogo(file);
  };

  const handleFaviconSelect = (file) => {
    setUploadedFavicon(file);
  };

  const handleLoginLogoSelect = (file) => {
    setUploadedLoginLogo(file);
  };

  const handleDiscard = () => {
    setUploadedLogo(null);
    setUploadedFullLogo(null);
    setUploadedFavicon(null);
    setUploadedLoginLogo(null);
    setResetKey((prev) => prev + 1);
    setLogoRefreshKey((prev) => prev + 1);
  };



const handleSave = async () => {
  if (!hasChanges) {
    showToast(
      TOAST_TYPE.ERROR,
      FEATURE_MSG.SYSTEM_SETTINGS.UPLOAD_AT_LEAST_ONE
    );
    return;
  }

  setIsSaving(true);

  try {
    if (uploadedLogo) {
      const formData = new FormData();
      formData.append("company_logo", uploadedLogo);

      await commonPostApi(API_ENDPOINTS.ADD_COMPANY_LOGO, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      showToast(
        TOAST_TYPE.SUCCESS,
        FEATURE_MSG.SYSTEM_SETTINGS.LOGO_UPLOADED
      );
    }

    if (uploadedFullLogo) {
      const formData = new FormData();
      formData.append("company_full_logo", uploadedFullLogo);

      await commonPostApi(API_ENDPOINTS.ADD_COMPANY_FULL_LOGO, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      showToast(
        TOAST_TYPE.SUCCESS,
        FEATURE_MSG.SYSTEM_SETTINGS.FULL_LOGO_UPLOADED
      );
    }

    if (uploadedFavicon) {
      const formData = new FormData();
      formData.append("favicon", uploadedFavicon);

      await commonPostApi(API_ENDPOINTS.ADD_FAVICON, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      showToast(
        TOAST_TYPE.SUCCESS,
        FEATURE_MSG.SYSTEM_SETTINGS.FAVICON_UPLOADED
      );
    }

    if (uploadedLoginLogo) {
      const formData = new FormData();
      formData.append("login_logo", uploadedLoginLogo);

      await commonPostApi(API_ENDPOINTS.ADD_LOGIN_LOGO, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      showToast(
        TOAST_TYPE.SUCCESS,
        FEATURE_MSG.SYSTEM_SETTINGS.LOGIN_LOGO_UPLOADED
      );
    }

    // Refresh preview after all uploads
    setLogoRefreshKey(prev => prev + 1);

    // Reset form after success
    handleDiscard();

  } catch (error) {
    showToast(
      TOAST_TYPE.ERROR,
      FEATURE_MSG.SYSTEM_SETTINGS.LOGO_UPLOAD_FAILED
    );
  } finally {
    setIsSaving(false);
  }
};
  const buttons = [
    {
      type: "primarySuccessButton",
      label: "Save Changes",
      class: "md:w-11rem md:pr-3 w-8rem",
      onClick: handleSave,
      disabled: !hasChanges || isSaving,
    },
    {
      type: "secondaryButton",
      label: "Cancel",
      class: "w-6rem ml-3",
      onClick: handleDiscard,
      disabled: !hasChanges || isSaving,
    },
  ];
  return (
    <div className="container mt-3 mb-3">
      <div className="px-2 md:px-3">
        <PrimaryHeading className={"mt-5 mb-3"} />
        <div style={{ color: "rgba(47, 43, 61, 0.9)" }}>
          <div>
            <ButtonWithSearch searchHeaderLable={"System Settings"} />
          </div>
          <div className="mt-3 md:px-3 px-2">
            <ImageUpload
              onFileSelect={handleLogoSelect}
              uploadLabel="Add Your Logo"
              previewLabel="Logo Preview"
              description={"Use a JPG, PNG or SVG smaller than 100KB."}
              resetTrigger={resetKey}
              logopreview={logoUrls.mini}
            />
          </div>
          <div
            style={{
            borderTop: "1px dashed rgba(47, 43, 61, 0.5)",
             margin: "30px 0"
            }}
            className="md:hidden"
          ></div>
          <div className="mt-3 md:px-3 px-2">
            <ImageUpload
              onFileSelect={handleFullLogoSelect}
              uploadLabel="Add Your Full Logo"
              previewLabel="Full Logo Preview"
              description={"Use a JPG, PNG or SVG smaller than 100KB."}
              resetTrigger={resetKey}
              logopreview={logoUrls.full}
            />
          </div>
           <div
            style={{
            borderTop: "1px dashed rgba(47, 43, 61, 0.34)",
             margin: "30px 0"
            }}
            className="md:hidden"
          ></div>
          <div className="mt-3 md:px-3 px-2">
            <ImageUpload
              onFileSelect={handleFaviconSelect}
              uploadLabel="Add Your Favicon"
              previewLabel="Favicon Preview"
              description={"Use a ICO smaller than 100KB."}
              resetTrigger={resetKey}
              isFavicon={true}
              logopreview={logoUrls.fav}
            />
          </div>
           <div
            style={{
            borderTop: "1px dashed rgba(47, 43, 61, 0.5)",
             margin: "30px 0"
            }}
            className="md:hidden"
          ></div>
          <div className="mt-3 md:px-3 px-2">
            <ImageUpload
              onFileSelect={handleLoginLogoSelect}
              uploadLabel="Add Login Logo"
              previewLabel="Login Logo Preview"
              description={"Use a JPG, PNG or SVG smaller than 100KB."}
              resetTrigger={resetKey}
              logopreview={logoUrls.login}
            />
          </div>
          <div className="pt-3 px-4">
            <AddButtonSection buttons={buttons} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default SystemSettings;
